/** Pagina impostazioni notifiche (SPEC-005). */

import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { PreferencesMatrix } from './PreferencesMatrix';
import { Alert, Badge, Button, Card, EmptyState, Field, Spinner, Switch } from '@/ui';
import {
  useCreateTelegramLinkCode,
  useDeleteChannel,
  useNotificationChannels,
  type NotificationChannelKind,
  usePatchChannel,
  useSendTestNotification,
  useSubscribePush,
  useUnsubscribePush,
  useUpsertEmail,
  useVapidPublicKey,
  type EmailConfig,
  type LinkCodeResponse,
  type NotificationChannel,
  type PushConfig,
  type TelegramConfig,
} from './api';
import './notifications.css';

function isTelegram(config: NotificationChannel['config']): config is TelegramConfig {
  return 'chatId' in config;
}

function isEmail(config: NotificationChannel['config']): config is EmailConfig {
  return 'address' in config;
}

function isPush(config: NotificationChannel['config']): config is PushConfig {
  return 'endpoint' in config;
}

function ChannelStatus({ channel }: { channel: NotificationChannel }) {
  if (channel.verifiedAt === null) {
    return <Badge tone="accent">In attesa di verifica</Badge>;
  }
  return channel.enabled ? <Badge tone="positive">Attivo</Badge> : <Badge tone="neutral">Disattivato</Badge>;
}

const TELEGRAM_LINK_POLL_INTERVAL_MS = 3000;

function TelegramSection() {
  const [link, setLink] = useState<LinkCodeResponse | null>(null);
  const [linkExpired, setLinkExpired] = useState(false);

  const channels = useNotificationChannels({
    refetchInterval: link && !linkExpired ? TELEGRAM_LINK_POLL_INTERVAL_MS : false,
  });
  const createCode = useCreateTelegramLinkCode();
  const patch = usePatchChannel();
  const remove = useDeleteChannel();

  const channel = useMemo(
    () => channels.data?.items.find((c) => c.channelType === 'telegram'),
    [channels.data],
  );

  useEffect(() => {
    if (!link || channel) return;
    const expiresInMs = new Date(link.expiresAt).getTime() - Date.now();
    if (expiresInMs <= 0) {
      setLinkExpired(true);
      return;
    }
    const timeout = setTimeout(() => setLinkExpired(true), expiresInMs);
    return () => clearTimeout(timeout);
  }, [link, channel]);

  useEffect(() => {
    if (channel) {
      setLink(null);
      setLinkExpired(false);
    }
  }, [channel]);

  const handleGenerate = () => {
    createCode.mutate(undefined, {
      onSuccess: (data) => setLink(data),
    });
  };

  return (
    <Card title="Telegram" subtitle="Ricevi le notifiche direttamente in chat">
      <div className="notifications__section">
        {channel ? (
          <>
            <div className="notifications__row">
              <ChannelStatus channel={channel} />
              {isTelegram(channel.config) && (
                <span className="notifications__meta">
                  {channel.config.username ?? `Chat ${channel.config.chatId}`}
                </span>
              )}
            </div>
            <div className="notifications__actions">
              <Switch
                label={channel.enabled ? 'Attivo' : 'Disattivato'}
                checked={channel.enabled}
                onChange={(checked) => patch.mutate({ type: 'telegram', enabled: checked })}
              />
              <Button variant="ghost" onClick={() => remove.mutate('telegram')}>
                Scollega
              </Button>
            </div>
          </>
        ) : (
          <>
            <p>Collega il bot di LabInvestment per ricevere le notifiche su Telegram.</p>
            <Button onClick={handleGenerate} loading={createCode.isPending}>
              Genera link di collegamento
            </Button>
            {link && (
              <div className="notifications__link">
                <p>
                  <a href={link.deepLink} target="_blank" rel="noreferrer">
                    Apri Telegram
                  </a>
                </p>
                <p className="notifications__code">Codice: {link.code}</p>
                {linkExpired ? (
                  <p className="notifications__hint">Codice scaduto, generane uno nuovo.</p>
                ) : (
                  <p className="notifications__hint">
                    Scade il {new Date(link.expiresAt).toLocaleString()} — in attesa di collegamento…
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function EmailSection() {
  const channels = useNotificationChannels();
  const upsert = useUpsertEmail();
  const patch = usePatchChannel();
  const remove = useDeleteChannel();
  const [address, setAddress] = useState('');

  const channel = useMemo(
    () => channels.data?.items.find((c) => c.channelType === 'email'),
    [channels.data],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    upsert.mutate({ address: address.trim() || undefined });
  };

  return (
    <Card title="Email" subtitle="Ricevi le notifiche al tuo indirizzo email">
      <div className="notifications__section">
        {channel ? (
          <>
            <div className="notifications__row">
              <ChannelStatus channel={channel} />
              {isEmail(channel.config) && (
                <span className="notifications__meta">{channel.config.address}</span>
              )}
            </div>
            {channel.verifiedAt === null && (
              <Alert variant="info" title="Verifica in corso">
                Clicca sul link che hai ricevuto per attivare il canale.
              </Alert>
            )}
            <div className="notifications__actions">
              <Switch
                label={channel.enabled ? 'Attivo' : 'Disattivato'}
                checked={channel.enabled}
                onChange={(checked) => patch.mutate({ type: 'email', enabled: checked })}
              />
              <Button variant="ghost" onClick={() => remove.mutate('email')}>
                Scollega
              </Button>
            </div>
          </>
        ) : (
          <form className="notifications__form" onSubmit={handleSubmit}>
            <Field
              label="Indirizzo email"
              type="email"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="lascia vuoto per usare quello dell'account"
            />
            <Button type="submit" loading={upsert.isPending}>
              Invia conferma
            </Button>
          </form>
        )}
      </div>
    </Card>
  );
}

function PushSection() {
  const channels = useNotificationChannels();
  const vapid = useVapidPublicKey();
  const subscribe = useSubscribePush();
  const unsubscribe = useUnsubscribePush();
  const [error, setError] = useState<string | null>(null);

  const pushChannels = useMemo(
    () => channels.data?.items.filter((c) => isPush(c.config)) ?? [],
    [channels.data],
  );

  const handleSubscribe = async () => {
    setError(null);
    if (!vapid.data?.publicKey) {
      setError('Chiave VAPID non disponibile');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setError('Il browser non supporta le notifiche push');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Permesso di notifica negato');
        return;
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.data.publicKey) as BufferSource,
      });

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        setError('Sottoscrizione push non valida');
        return;
      }

      subscribe.mutate({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: navigator.userAgent,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante la sottoscrizione');
    }
  };

  return (
    <Card title="Notifiche browser" subtitle="Ricevi gli avvisi su questo dispositivo">
      <div className="notifications__section">
        {error && (
          <Alert variant="error" title="Errore">
            {error}
          </Alert>
        )}
        {pushChannels.length === 0 ? (
          <EmptyState
            icon="🔔"
            title="Nessun dispositivo registrato"
            description="Attiva le notifiche su questo browser per ricevere gli avvisi in tempo reale."
          />
        ) : (
          <ul className="notifications__devices">
            {pushChannels.map((channel) => (
              <li key={channel.id} className="notifications__device">
                {isPush(channel.config) && (
                  <span className="notifications__device-name">
                    {channel.config.userAgent ?? channel.config.endpoint}
                  </span>
                )}
                <Button variant="ghost" onClick={() => unsubscribe.mutate(channel.id)}>
                  Rimuovi
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Button onClick={handleSubscribe} loading={subscribe.isPending || vapid.isPending}>
          Attiva su questo browser
        </Button>
      </div>
    </Card>
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData.split('').map((char) => char.charCodeAt(0)));
}

export function NotificationsPage() {
  const channels = useNotificationChannels();
  const sendTest = useSendTestNotification();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.register('/sw.js');
    }
  }, []);

  // Un canale conta come collegato solo se VERIFICATO: una casella accesa su un
  // canale non verificato prometterebbe un recapito che non avverrebbe.
  const verified = new Set(
    (channels.data?.items ?? []).filter((channel) => channel.verifiedAt !== null).map((channel) => channel.channelType),
  );
  const configured: Record<NotificationChannelKind, boolean> = {
    telegram: verified.has('telegram'),
    email: verified.has('email'),
    push: verified.has('push'),
  };

  if (channels.isPending) return <Spinner label="Caricamento impostazioni notifiche…" />;
  if (channels.isError) {
    const message = channels.error instanceof ApiError ? channels.error.message : 'Errore imprevisto.';
    return (
      <Alert variant="error" title="Impossibile caricare le impostazioni">
        {message}
      </Alert>
    );
  }

  return (
    <div className="notifications">
      <header className="notifications__header">
        <div>
          <h1 className="notifications__title">Notifiche</h1>
          <p className="notifications__subtitle">
            Scegli dove ricevere gli avvisi del tuo portafoglio
          </p>
        </div>
        <Button onClick={() => sendTest.mutate()} loading={sendTest.isPending}>
          Invia notifica di prova
        </Button>
      </header>

      {sendTest.isError && (
        <Alert variant="error" title="Impossibile inviare la notifica di prova">
          {sendTest.error instanceof ApiError ? sendTest.error.message : 'Errore imprevisto.'}
        </Alert>
      )}

      {/* F-101: la matrice sta PRIMA dei canali. Chi apre questa pagina di
          solito vuole cambiare cosa gli arriva, non ricollegare Telegram —
          collegare un canale si fa una volta, scegliere gli avvisi si torna a
          farlo. */}
      <PreferencesMatrix configured={configured} />

      <div className="notifications__grid">
        <TelegramSection />
        <EmailSection />
        <PushSection />
      </div>
    </div>
  );
}
